import { Colors, Heading, Stepper } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ContextStep } from './ContextStep';
import { EmployeeAndTopicStep } from './EmployeeAndTopicStep';
import { PlanningDialogShell } from './PlanningDialogShell';
import { StepNavigation } from './StepNavigation';
import { TemplateStep } from './TemplateStep';
import { newMeetingSchema } from './validation';

import type { MeetingTemplateChoice } from './TemplateStep';
import type { NewMeetingFormData } from './validation';
import type { TranslationKey } from '@/i18n-key';
import type { ThematicMeetingTopic } from '@/types/thematic-meetings';
import type { FormEvent } from 'react';

import { useCreateThematicMeeting } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { meetingPlanPath } from '@/navigation/thematicMeetingsRoutes';
import { MEETINGS_TITLE } from '@/pages/ThematicMeetings/components/meetingStyles';
import { validateMeetingContext } from '@/pages/ThematicMeetings/utils/meetingRichText';
import { getPlanNavigationState } from '@/pages/ThematicMeetings/utils/planNavigationState';
import { showSuccessToast } from '@/plugins/Toast';

/** Mounted only while open: every opening starts from a fresh form. */
interface NewMeetingModalProps {
    onClose: () => void;
    /** An employee already picked before the modal opened (e.g. a row in the overdue-employees
     * dialog): pre-fills the first screen's employee field so the flow opens straight on the topic
     * choice instead of asking the manager to search for someone they already picked. */
    initialEmployee?: string;
    /** A topic already decided before the modal opened (e.g. the profile's growth tab asking for a
     * career conversation): pre-checks its tile on the first screen. Handed over together with the
     * employee, it leaves that screen with nothing to ask and the flow opens on the plan choice. */
    initialTopic?: ThematicMeetingTopic;
}

/** The two steps named in the step bar, in order. */
const STEPS = [
    {
        key: 'employeeAndTopic',
        labelKey: 'thematicMeetings.newMeeting.stepEmployeeAndTopic',
    },
    {
        key: 'context',
        labelKey: 'thematicMeetings.newMeeting.stepContext',
    },
] as const satisfies readonly { key: string; labelKey: TranslationKey }[];

type StepKey = (typeof STEPS)[number]['key'];

/**
 * The screens of the flow, in order: the step of the bar each one belongs to, and the title drawn
 * above it. Choosing the plan is a second screen of the first step rather than a step of its own —
 * the bar keeps naming the two things the manager decides, who the meeting is with and what it is
 * about, then the context.
 */
const PANES = [
    {
        key: 'employeeAndTopic',
        step: 'employeeAndTopic',
        titleKey: 'thematicMeetings.newMeeting.title',
    },
    {
        key: 'template',
        step: 'employeeAndTopic',
        titleKey: 'thematicMeetings.newMeeting.templateTitle',
    },
    {
        key: 'context',
        step: 'context',
        titleKey: 'thematicMeetings.newMeeting.contextLabel',
    },
] as const satisfies readonly { key: string; step: StepKey; titleKey: TranslationKey }[];

type PaneKey = (typeof PANES)[number]['key'];

const paneIndexOf = (key: PaneKey) => PANES.findIndex((pane) => pane.key === key);

const FIRST_PANE_INDEX = 0;
const LAST_PANE_INDEX = PANES.length - 1;

/** The first step's first screen is answered once the meeting knows who it is with and what it is
 *  about; its second screen still has the plan to ask for. */
const isEmployeeAndTopicAnswered = (employeeId?: string, topic?: ThematicMeetingTopic) =>
    Boolean(employeeId && topic);

export const NewMeetingModal = ({
    onClose,
    initialEmployee,
    initialTopic,
}: NewMeetingModalProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const { mutateAsync: createMeeting, isPending: isCreating } = useCreateThematicMeeting();

    // Opens on the first screen that is not already answered. A caller who hands over both the
    // employee and the topic — the growth tab's "Réévaluer" does — has already answered the first
    // screen, and asking the manager to answer it again is a screen for nothing. The plan is still
    // its own choice, so the flow opens on it rather than skipping the whole step.
    const [currentPane, setCurrentPane] = useState(() =>
        isEmployeeAndTopicAnswered(initialEmployee, initialTopic)
            ? paneIndexOf('template')
            : FIRST_PANE_INDEX,
    );
    // The plan the meeting will follow. Kept beside the form rather than in it because a topic changed
    // on the way back invalidates it, which the form's own reset would not know to do.
    const [selectedTemplate, setSelectedTemplate] = useState<MeetingTemplateChoice | null>(null);
    const stepRegionRef = useRef<HTMLDivElement>(null);
    const isInitialStepRef = useRef(true);

    const {
        handleSubmit,
        setValue,
        watch,
        getValues,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<NewMeetingFormData>({
        resolver: zodResolver(newMeetingSchema),
        defaultValues: { employeeId: initialEmployee ?? '', topic: initialTopic, context: '' },
    });

    const employeeId = watch('employeeId');
    const topic = watch('topic');
    const context = watch('context') ?? '';

    const steps = STEPS.map(({ key, labelKey }) => ({ key, label: t(labelKey) }));

    const currentPaneKey: PaneKey = PANES[currentPane].key;
    const currentStepIndex = STEPS.findIndex(({ key }) => key === PANES[currentPane].step);

    // What the screen the manager is on still has to be given before the flow can move on.
    const isCurrentPaneIncomplete =
        (currentPaneKey === 'employeeAndTopic' && !isEmployeeAndTopicAnswered(employeeId, topic)) ||
        (currentPaneKey === 'template' && !selectedTemplate);

    const stepProgressLabel = t('thematicMeetings.newMeeting.stepProgress', {
        current: currentStepIndex + 1,
        total: STEPS.length,
        label: steps[currentStepIndex].label,
    });

    // Move focus to the step region whenever the screen changes (but not on the initial open, where
    // the dialog shell already placed focus), so screen-reader users land on the new screen and hear
    // which step they are on instead of keeping focus on a control that is no longer relevant.
    useEffect(() => {
        if (isInitialStepRef.current) {
            isInitialStepRef.current = false;
            return;
        }
        stepRegionRef.current?.focus();
    }, [currentPane]);

    const goToNextPane = () => setCurrentPane((pane) => Math.min(pane + 1, LAST_PANE_INDEX));
    const goToPreviousPane = () => setCurrentPane((pane) => Math.max(pane - 1, FIRST_PANE_INDEX));

    // A plan belongs to one topic, so a topic changed on the way back leaves the plan chosen for
    // the previous one behind: it is asked for again rather than carried over.
    const selectTopic = (nextTopic: ThematicMeetingTopic) => {
        setValue('topic', nextTopic, { shouldValidate: true });
        setSelectedTemplate(null);
    };

    // The context is the one answer that can be refused, and its message belongs under the field
    // the manager is still looking at: checked here so the schema's raw message never surfaces
    // untranslated from the submit that would otherwise reject it.
    const isContextAccepted = () => {
        const errorMessage = validateMeetingContext(getValues('context') ?? '', t);
        if (errorMessage) {
            setError('context', { message: errorMessage });
            return false;
        }
        clearErrors('context');
        return true;
    };

    const submitForm = handleSubmit(async (data) => {
        // The plan is asked for before this screen is reachable, so it is always there by now; the
        // guard is what tells TypeScript that, rather than a case the manager can produce.
        if (!selectedTemplate) {
            return;
        }

        try {
            const meeting = await createMeeting({
                employeeId: data.employeeId,
                meetingPlanTemplateId: selectedTemplate,
                // The plan's edit sets the date; until then the invitation carries a default slot.
                scheduledAt: null,
                // No duration: the plan's own agenda says how long the meeting runs, and the
                // invitation is measured from it.
                context: data.context || null,
            });

            showSuccessToast({ message: t('thematicMeetings.newMeeting.createSuccess') });

            onClose();
            navigate({
                path: meetingPlanPath(meeting.id),
                state: getPlanNavigationState(data.context),
            });
        } catch (error) {
            handleError({
                error,
                data: {
                    title: t('errors.genericTitle'),
                    message: t('thematicMeetings.newMeeting.createError'),
                },
            });
        }
    });

    // A real <form>, so Enter advances to the next screen. On the last one it does nothing: only the
    // button creates the meeting.
    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (currentPane === LAST_PANE_INDEX || isCurrentPaneIncomplete) {
            return;
        }
        goToNextPane();
    };

    const createIfContextAccepted = () => {
        if (isContextAccepted()) {
            void submitForm();
        }
    };

    return (
        <PlanningDialogShell
            isOpen
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            // The window is named after the control that opened it — « Préparer une rencontre » —
            // not after the screen being shown. Constant across all of them, and distinct from
            // every screen title, so a screen reader hears the window once and each screen once.
            title={t('thematicMeetings.list.prepareMeeting')}
            dataTestId={'new-meeting-modal'}
            onSubmit={handleFormSubmit}
            stepBar={
                <Stepper
                    variant={'pills'}
                    highlightReachedConnectors
                    compactBelowSm
                    steps={steps}
                    current={currentStepIndex + 1}
                    ariaLabel={t('thematicMeetings.newMeeting.stepsLabel')}
                    stepStatusLabels={{
                        completed: t('thematicMeetings.newMeeting.stepStatus.completed'),
                        current: t('thematicMeetings.newMeeting.stepStatus.current'),
                        upcoming: t('thematicMeetings.newMeeting.stepStatus.upcoming'),
                    }}
                    dataTestId={'new-meeting-stepper'}
                />
            }
            footer={
                <StepNavigation
                    isLastStep={currentPane === LAST_PANE_INDEX}
                    canGoBack={currentPane > FIRST_PANE_INDEX}
                    isNextDisabled={isCurrentPaneIncomplete}
                    isCreating={isCreating}
                    onBack={goToPreviousPane}
                    onCreate={createIfContextAccepted}
                />
            }
        >
            <div
                ref={stepRegionRef}
                tabIndex={-1}
                role={'group'}
                aria-label={stepProgressLabel}
                className={'flex flex-col gap-[24px] focus:outline-none'}
                data-testid={'new-meeting-step-region'}
            >
                {/* Drawn here rather than by the dialog: in this chrome the window's own title is
                    kept for the screen reader alone, because a screen's title belongs to the screen.
                    Level 4 follows the dialog's own level 3 without skipping. */}
                {/* 28px lines, as the design sets every screen title of the flow: the shared title
                    size alone would space it for a page. */}
                <Heading
                    level={4}
                    color={Colors.NAVY}
                    className={`${MEETINGS_TITLE} leading-[28px]`}
                >
                    {t(PANES[currentPane].titleKey)}
                </Heading>

                {currentPaneKey === 'employeeAndTopic' && (
                    <EmployeeAndTopicStep
                        employeeId={employeeId}
                        topic={topic ?? null}
                        initialEmployeeId={initialEmployee}
                        onEmployeeChange={(nextEmployeeId) =>
                            setValue('employeeId', nextEmployeeId, { shouldValidate: true })
                        }
                        onSelectTopic={selectTopic}
                    />
                )}

                {currentPaneKey === 'template' && topic && (
                    <TemplateStep
                        topic={topic}
                        selectedTemplate={selectedTemplate}
                        onSelectTemplate={setSelectedTemplate}
                    />
                )}

                {currentPaneKey === 'context' && (
                    <ContextStep
                        context={context}
                        errorMessage={errors.context?.message}
                        onContextChange={(value) => setValue('context', value)}
                    />
                )}
            </div>
        </PlanningDialogShell>
    );
};
